import styled, { css } from 'styled-components';
import { MdClose, MdError } from 'react-icons/md';
import { Input as InputRS } from 'reactstrap';

const getSemanticSurfaceCard = ({ theme }) =>
  theme.colors.semantic.surface.card;

const getSemanticSurfaceMuted = ({ theme }) =>
  theme.colors.semantic.surface.muted;

const getSemanticTextPrimary = ({ theme }) =>
  theme.colors.semantic.text.primary;

const getSemanticTextSecondary = ({ theme }) =>
  theme.colors.semantic.text.secondary;

const getSemanticTextTertiary = ({ theme }) => theme.colors.lowBlack;

const getSemanticBorderDefault = ({ theme }) =>
  theme.colors.semantic.border.default;

const getSemanticActionPrimary = ({ theme }) =>
  theme.colors.semantic.action.primary;

const getFocusColor = ({ theme, primaryColor }) =>
  theme.colors[primaryColor] ||
  primaryColor ||
  getSemanticActionPrimary({ theme });

const iconModifiers = {
  small: () => css`
    padding: 8px 0 8px 8px;

    svg {
      height: 16px;
      width: auto;
    }
  `,
  normal: () => css`
    padding: 8px 0 8px 8px;

    svg {
      height: 16px;
      width: auto;
    }
  `,
  large: () => css`
    padding: 0;

    svg {
      height: 24px;
      width: auto;
    }
  `,
};

const clearIconModifiers = {
  small: () => css`
    font-size: 16px;
  `,
  normal: () => css`
    font-size: 16px;
  `,
  large: () => css`
    font-size: 24px;
  `,
};

const inputHeightSizeModifiers = {
  small: () => css`
    height: 32px;
    font-size: 14px;
  `,
  normal: () => css`
    height: 38px;
    font-size: 14px;
  `,
  large: () => css`
    height: 52px;
    font-size: 16px;
  `,
};

const variantModifiers = {
  rounded: () => css`
    padding: 8px 16px;
    border-radius: 32px;
  `,
  normal: () => css`
    padding: 6px 8px;
    border-radius: 8px;
  `,
};

export const Input = styled(InputRS)`
  font-family: 'Nunito Sans', sans-serif;
  width: 100%;
  box-sizing: border-box;
  box-shadow: none !important;
  padding: 6px 8px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  font-weight: 400;

  ${props => inputHeightSizeModifiers[props.$size]};

  caret-color: ${({ readonly, theme }) =>
    readonly ? 'transparent' : getSemanticTextPrimary({ theme })};

  color: ${getSemanticTextPrimary} !important;
  -webkit-text-fill-color: ${getSemanticTextPrimary};

  &:disabled {
    background-color: ${getSemanticSurfaceMuted};
    opacity: 50%;
  }

  &:focus-within {
    outline: none;
    background-color: transparent;
    box-shadow: none !important;
    border-color: transparent !important;
  }

  :focus,
  :focus-visible {
    outline: none;
    background-color: transparent;
    box-shadow: none !important;
    border-color: transparent !important;
  }

  &.form-control:focus,
  &.form-control:focus-visible {
    outline: none;
    background-color: transparent;
    box-shadow: none !important;
    border-color: transparent !important;
  }

  &::placeholder {
    color: ${getSemanticTextTertiary};
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    color: ${getSemanticTextPrimary};
    -webkit-text-fill-color: ${getSemanticTextPrimary};
    -webkit-box-shadow: 0 0 0px 1000px transparent inset;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  [type='number'] {
    -moz-appearance: textfield;
  }

  :read-only {
    color: ${getSemanticTextPrimary};
    background-color: transparent;
  }
`;

export const Wrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const LabelWrapper = styled.div`
  display: inline-flex;
  margin-bottom: 8px;
`;

export const Label = styled.label`
  color: ${getSemanticTextSecondary};
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.25px;
  margin: 0;
`;

export const Error = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.25px;
  color: ${({ theme }) => theme.colors.danger500};
`;

export const IconError = styled(MdError).attrs({
  size: 16,
})`
  color: ${({ theme }) => theme.colors.danger500};
`;

export const InputGroup = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
  transition: border 0.1s ease-in, box-shadow 0.1s ease-in;
  background-color: ${getSemanticSurfaceCard};

  ${props => inputHeightSizeModifiers[props.size]};
  ${props => variantModifiers[props.variant]};

  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.colors.danger500 : getSemanticBorderDefault({ theme })};

  &:hover {
    border-color: ${({ theme, error, primaryColor }) =>
      error ? theme.colors.danger500 : getFocusColor({ theme, primaryColor })};
  }

  &:focus-visible,
  &:focus-within,
  &:focus {
    outline: none;

    ${({ theme, error, primaryColor }) => css`
      border: 1px solid
        ${error
          ? theme.colors.danger500
          : getFocusColor({ theme, primaryColor })};
      box-shadow: ${error
        ? 'none'
        : `0 0 0 0.2rem ${theme.colors.semantic.surface.selection}`};
      svg {
        color: ${error
          ? theme.colors.danger500
          : getFocusColor({ theme, primaryColor })};
        transition: color 0.2s ease-in;
      }

      button {
        border-left-color: ${error
          ? theme.colors.danger500
          : getFocusColor({ theme, primaryColor })};
        transition: border-left-color 0.2s ease-in;
      }
    `}
  }
`;

export const InputIcon = styled.span`
  display: flex;
  align-items: center;
  padding: 8px 0 8px 8px;
  height: 100%;
  color: ${({ theme, error }) =>
    error ? theme.colors.danger500 : theme.colors.semantic.text.secondary};

  ${props => iconModifiers[props.$size]};
`;

export const ClearFieldButton = styled.button`
  width: auto;
  height: 100%;
  background-color: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  :focus,
  :focus-visible {
    outline: none;
  }

  :hover {
    opacity: 0.7;
  }
  transition: opacity 0.1s ease-in;
`;

export const ClearIcon = styled(MdClose)`
  color: ${getSemanticTextTertiary};
  transition: color 0.1s ease-in;
  ${props => clearIconModifiers[props.$size]};
`;

export const ClipboardButton = styled.button`
  width: auto;
  height: 100%;
  background-color: ${getSemanticSurfaceMuted};
  border: none;
  border-left: 1px solid ${getSemanticBorderDefault};
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  color: ${getSemanticTextSecondary};
  :focus,
  :focus-visible {
    outline: none;
  }

  :hover {
    opacity: 0.7;
  }
  transition: opacity 0.1s ease-in;

  svg {
    color: ${getSemanticTextSecondary};
    transition: color 0.1s ease-in;
  }
`;
